import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatToolbar } from '@angular/material/toolbar';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { I18nPipe } from './core/i18n/i18n.pipe';
import type { TranslationKey } from './core/i18n/translations';
import { SupabaseService } from './core/services/supabase.service';
import { ActiveTimerNotificationService } from './core/services/active-timer-notification.service';
import { TimeTrackingService } from './features/time-tracking/data-access/time-tracking.service';
import { PageContainerComponent } from './shared/ui/page-container/page-container.component';
import { DomainsService } from './features/domains/data-access/domains.service';
import { Domain } from './features/domains/models/domain.model';
import { domainColorHex } from './features/domains/models/domain-color.model';

interface NavigationSubItem {
  labelKey: TranslationKey;
  queryParams: Record<string, string>;
}

interface NavigationItem {
  route: string;
  labelKey: TranslationKey;
  icon: 'today' | 'tasks' | 'calendar' | 'goals' | 'notes' | 'activities' | 'sport' | 'lists' | 'measurements' | 'meals' | 'culture' | 'domains';
  children?: readonly NavigationSubItem[];
}

@Component({
  standalone: true,
  selector: 'app-root',
  imports: [RouterModule, FormsModule, MatToolbar, MatButton, PageContainerComponent, I18nPipe],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  readonly appVersion = '1.2.0';
  compactToolbar = typeof window !== 'undefined' && window.innerWidth <= 599;

  @HostListener('window:resize')
  updateCompactToolbar(): void {
    this.compactToolbar = window.innerWidth <= 599;
  }

  readonly navigationItems: readonly NavigationItem[] = [
    { route: '/today', labelKey: 'product.today', icon: 'today' },
    {
      route: '/tasks', labelKey: 'product.tasks', icon: 'tasks',
      children: [
        { labelKey: 'product.todo', queryParams: { tab: 'todo' } },
        { labelKey: 'tasks.recurrent', queryParams: { tab: 'recurrent' } },
        { labelKey: 'product.routines', queryParams: { tab: 'routines' } }
      ]
    },
    { route: '/calendar', labelKey: 'product.calendar', icon: 'calendar' },
    {
      route: '/goals', labelKey: 'product.goals', icon: 'goals',
      children: [
        { labelKey: 'goals.period.daily', queryParams: { period: 'DAILY' } },
        { labelKey: 'goals.period.weekly', queryParams: { period: 'WEEKLY' } },
        { labelKey: 'goals.period.monthly', queryParams: { period: 'MONTHLY' } },
        { labelKey: 'goals.period.quarterly', queryParams: { period: 'QUARTERLY' } },
        { labelKey: 'goals.period.yearly', queryParams: { period: 'YEARLY' } }
      ]
    },
    { route: '/activities', labelKey: 'product.activities', icon: 'activities' },
    { route: '/lists', labelKey: 'product.lists', icon: 'lists' },
    { route: '/notes', labelKey: 'product.notes', icon: 'notes' },
    { route: '/measurements', labelKey: 'product.measurements', icon: 'measurements' },
    { route: '/meals', labelKey: 'product.meals', icon: 'meals' },
    { route: '/sport', labelKey: 'product.sport', icon: 'sport' },
    { route: '/culture', labelKey: 'culture.title', icon: 'culture' },
    { route: '/domains', labelKey: 'product.domains', icon: 'domains' }
  
  ];

  navigationOpen = false;
  domains: Domain[] = [];
  selectedDomainId: number | null = null;
  readonly expandedNavigationGroups = new Set<string>();

  private routerSubscription: Subscription | null = null;

  @ViewChild('navigationToggle')
  private navigationToggle?: ElementRef<HTMLButtonElement>;

  @ViewChild('firstNavigationLink')
  private firstNavigationLink?: ElementRef<HTMLAnchorElement>;

  constructor(
    public readonly sb: SupabaseService,
    private readonly timerNotifications: ActiveTimerNotificationService,
    public readonly timeTracking: TimeTrackingService,
    private readonly router: Router,
    private readonly domainsService: DomainsService
  ) {}

  async ngOnInit(): Promise<void> {
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.syncDomainFromUrl();
        void this.refreshActiveTimers();
      });

    await this.sb.ensureSession();
    if (this.sb.isLoggedIn) {
      await this.loadDomains();
      this.syncDomainFromUrl();
    }
    await this.refreshActiveTimers();
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;
  }

  get activeDomain(): Domain | null {
    return this.domains.find(domain => domain.id === this.selectedDomainId) ?? null;
  }

  get domainNavigationQueryParams(): Record<string, number> | null {
    return this.selectedDomainId === null ? null : { domain: this.selectedDomainId };
  }

  childNavigationQueryParams(child: NavigationSubItem): Record<string, string | number> {
    return this.selectedDomainId === null
      ? { ...child.queryParams }
      : { ...child.queryParams, domain: this.selectedDomainId };
  }

  domainOptionColor(domain: Domain): string {
    return domainColorHex(domain.color, 1);
  }

  async changeGlobalDomain(value: number | null): Promise<void> {
    this.selectedDomainId = value;
    const tree = this.router.parseUrl(this.router.url);
    const queryParams = { ...tree.queryParams };
    if (value === null) delete queryParams['domain'];
    else queryParams['domain'] = value;
    tree.queryParams = queryParams;
    window.location.assign(tree.toString());
  }

  get activeTimerCount(): number {
    return this.timeTracking.activeCount();
  }

  get currentUrl(): string {
    return this.router.url;
  }

  get showHomeReturn(): boolean {
    const path = this.currentPath;
    return this.showAuthenticatedShell && path !== '/';
  }

  get showAuthenticatedShell(): boolean {
    return this.sb.isLoggedIn && !this.isPublicAuthRoute(this.currentPath);
  }

  toggleNavigation(): void {
    this.navigationOpen = !this.navigationOpen;

    if (this.navigationOpen) {
      queueMicrotask(() => this.firstNavigationLink?.nativeElement.focus());
    }
  }

  toggleNavigationGroup(route: string): void {
    if (this.expandedNavigationGroups.has(route)) {
      this.expandedNavigationGroups.delete(route);
    } else {
      this.expandedNavigationGroups.add(route);
    }
  }

  isNavigationGroupExpanded(route: string): boolean {
    return this.expandedNavigationGroups.has(route);
  }

  isNavigationRouteActive(route: string): boolean {
    return this.currentPath === route || this.currentPath.startsWith(`${route}/`);
  }

  closeNavigation(restoreFocus = false): void {
    this.navigationOpen = false;

    if (restoreFocus) {
      queueMicrotask(() => this.navigationToggle?.nativeElement.focus());
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.navigationOpen) {
      this.closeNavigation(true);
    }
  }

  async logout(): Promise<void> {
    this.closeNavigation();
    await this.timerNotifications.unregisterDevice();
    await this.sb.signOut();
    this.timeTracking.clearActiveEntries();
    await this.router.navigateByUrl('/login');
  }

  private async loadDomains(): Promise<void> {
    try {
      this.domains = (await this.domainsService.getDomains())
        .filter(domain => domain.archivedAt === null);
    } catch {
      this.domains = [];
    }
  }

  private syncDomainFromUrl(): void {
    const raw = this.router.parseUrl(this.router.url).queryParams['domain'];
    if (raw === undefined || raw === null || raw === '') {
      this.selectedDomainId = null;
      return;
    }
    const id = Number(raw);
    this.selectedDomainId = Number.isInteger(id) && id !== 0 ? id : null;
  }

  private async refreshActiveTimers(): Promise<void> {
    if (!this.sb.isLoggedIn) {
      this.timeTracking.clearActiveEntries();
      return;
    }

    try {
      await this.timeTracking.refreshActiveEntries();
    } catch {
      // Preserve the last known state on a transient refresh failure.
    }
  }

  private get currentPath(): string {
    return this.router.url.split('?')[0].split('#')[0];
  }

  private isPublicAuthRoute(path: string): boolean {
    return ['/login', '/signup', '/forgot-password', '/reset-password'].includes(path);
  }
}
